import React, { useCallback, useMemo } from "react"

import Slider from "rc-slider"
import "rc-slider/assets/index.css"

import styles from "./FunctionVisitCountsSlider.module.scss"

// values length must be 4.
// values means [min_visit_count_for_claiming_conversion, min_visit_count_for_manually_claiming_cancel_fee, min_visit_count_for_encourageing_rereservation, min_visit_count_for_nothing]
type FunctionVisitCountsSliderProps = {
	values: number[]
	disabled?: boolean
	onValuesChange: (values: number[]) => void
}

const min = 0
const max = 20
const RESET_VALUES = [4, 8, 12, 16]

// segments will be [[0, 4], [4, 8], [8, 12], [12, 16], [16, 20]] when reset button
type SegmentDetail = {
	index: number
	start: number
	end: number
	length: number
}

const SEGMENT_COLORS = [
	"#FF6B6B", // 赤 - 即時請求
	"#FF8C42", // オレンジ - 請求転換
	"#FFD166", // 黄色 - 手動請求
	"#06D6A0", // 緑 - 再予約案内
	"#B8BCC8", // 灰色 - 使用機能なし
]

const SEGMENT_NAMES = [
	"即時請求",
	"請求転換",
	"手動請求",
	"再予約案内",
	"使用機能なし",
]

const formatVisitCounts = (segment: SegmentDetail): string => {
	if (segment.start === segment.end) {
		return "無効"
	}

	// 20 という数字はバックエンド側で 9999 に変換するので、そのように表示しておく。
	const from = segment.start >= 20 ? 9999 : segment.start
	const until = segment.end >= 20 ? 9999 : segment.end - 1
	if (segment.index === 4) {
		return `${from} 回以上`
	} else if (from === until) {
		return `${from} 回`
	}

	return `${from} ~ ${until} 回`
}

const convertValuesToSegmentDetails = (values: number[]): SegmentDetail[] => {
	const sortedValues = [...values].sort((a, b) => a - b)
	const positions = [min, ...sortedValues, max]

	return Array.from({ length: positions.length - 1 }, (_, i) => ({
		index: i,
		start: positions[i],
		end: positions[i + 1],
		length: positions[i + 1] - positions[i],
	}))
}

// マーク（固定目盛り）の設定。5の倍数のみ表示。
const marks = (() => {
	const markObj: Record<number, string> = {}
	for (let i = min; i < max; i++) {
		// maxを除外
		if (i % 5 === 0) {
			markObj[i] = i.toString()
		}
	}
	// 右端に9999を表示
	markObj[max] = "9999"
	return markObj
})()

export const FunctionVisitCountsSlider: React.FC<
	FunctionVisitCountsSliderProps
> = ({ values, onValuesChange, disabled = false }) => {
	const segments = useMemo((): SegmentDetail[] => {
		return convertValuesToSegmentDetails(values)
	}, [values])

	const handleChange = useCallback(
		(newValues: number[]) => {
			onValuesChange(newValues)
		},
		[onValuesChange]
	)

	const handleReset = useCallback(() => {
		onValuesChange(RESET_VALUES)
	}, [onValuesChange])

	// カスタムトラックスタイルの設定
	const trackStyles = useMemo(() => {
		const sortedValues = [...values].sort((a, b) => a - b)
		const totalRange = max - min

		// 各セグメントのスタイルを生成
		const tracks = sortedValues.map((value, index) => {
			const prevValue = index === 0 ? min : sortedValues[index - 1]
			const left = ((prevValue - min) / totalRange) * 100
			const width = ((value - prevValue) / totalRange) * 100
			return {
				left: `${left}%`,
				width: `${width}%`,
				backgroundColor: SEGMENT_COLORS[index],
			}
		})

		// 最後のセグメント
		if (sortedValues.length > 0) {
			const lastValue = sortedValues[sortedValues.length - 1]
			const left = ((lastValue - min) / totalRange) * 100
			const width = ((max - lastValue) / totalRange) * 100
			tracks.push({
				left: `${left}%`,
				width: `${width}%`,
				backgroundColor: SEGMENT_COLORS[sortedValues.length],
			})
		}

		return tracks
	}, [values])

	return (
		<div className={styles.container}>
			<div className={styles.controlsWrapper}>
				{!disabled && (
					<button
						className={styles.resetButton}
						onClick={(e) => {
							e.preventDefault()
							handleReset()
						}}
					>
						リセット
					</button>
				)}
				{segments.map((segment) => {
					const isInvalid = segment.start === segment.end
					return (
						<div key={segment.index} className={styles.segmentInfo}>
							<div
								className={styles.colorBox}
								style={{ backgroundColor: SEGMENT_COLORS[segment.index] }}
							/>
							<span
								className={`${styles.segmentLabel} ${
									isInvalid
										? styles.segmentLabelInvalid
										: styles.segmentLabelActive
								}`}
							>
								{SEGMENT_NAMES[segment.index]}: {formatVisitCounts(segment)}
							</span>
						</div>
					)
				})}
			</div>
			<div className={styles.sliderWrapper}>
				{/* カスタムトラック */}
				<div className={styles.customTrack}>
					{trackStyles.map((style, index) => (
						<div key={index} className={styles.trackSegment} style={style} />
					))}
				</div>
				<Slider
					range
					min={min}
					max={max}
					step={1}
					value={values}
					onChange={(value) => {
						if (Array.isArray(value)) {
							handleChange(value)
						}
					}}
					pushable={0} // 重なりを許可
					allowCross={true} // 交差を許可
					marks={marks}
					disabled={disabled}
				/>
			</div>
		</div>
	)
}
